// Port of the accepted mobile search; traversal, limits and ACL-backed listing are unchanged.
import { childLocation, type ExplorerEntry, type ExplorerListing, type FolderLocation } from './explorerApi';

export type FileSearch = { name:string; extensions:string; from:string; to:string; recursive:boolean };
export const emptySearch:FileSearch={name:'',extensions:'',from:'',to:'',recursive:false};
export type SearchProgress={files:ExplorerEntry[];folders:number;checked:number;skipped:number;limited:boolean};
const normalize=(value:string)=>value.toLocaleLowerCase('ru').replaceAll('ё','е');
export const fileLocation=(at:FolderLocation,file:ExplorerEntry)=>file.location||childLocation(at,file.name);
export const fileKey=(file:ExplorerEntry)=>file.location?`${file.location.rootId}:${file.location.path}`:file.name;
export function localDay(value:string) {
  if(!/^\d{4}-\d{2}-\d{2}$/.test(value))return null;
  const [year,month,day]=value.split('-').map(Number),date=new Date(year,month-1,day);
  return date.getFullYear()===year&&date.getMonth()===month-1&&date.getDate()===day?date:null;
}
export function searchError(value:FileSearch) {
  if((value.from&&!localDay(value.from))||(value.to&&!localDay(value.to)))return 'Выберите корректные даты.';
  if(value.from&&value.to&&value.from>value.to)return 'Дата «По» должна быть не раньше даты «С».';
  if(value.extensions.split(/[,;\s]+/).filter(Boolean).some(ext=>! /^(?:\*\.|\.)?[\p{L}\p{N}]+$/u.test(ext)))return 'Укажите расширения через запятую: jpg, png, mp4.';
  return '';
}
export function matchesSearch(file:ExplorerEntry,value:FileSearch) {
  if(!normalize(file.name).includes(normalize(value.name.trim())))return false;
  const extensions=value.extensions.split(/[,;\s]+/).filter(Boolean).map(ext=>normalize(ext.replace(/^\*?\./,'')));
  if(extensions.length&&(file.isFolder||!extensions.includes(normalize(file.name.split('.').length>1?file.name.split('.').at(-1)!:''))))return false;
  if(value.from||value.to){
    if(!Number.isFinite(file.modified)||file.modified<=0)return false;
    const from=localDay(value.from),to=localDay(value.to),time=file.modified*1000;
    if(from&&time<from.getTime())return false;
    if(to){to.setDate(to.getDate()+1);if(time>=to.getTime())return false;}
  }
  return true;
}

// One folder request at a time. Every listing uses the existing authenticated
// API and its current ACL; no file contents are read or downloaded by search.
export async function searchFolders(at:FolderLocation,value:FileSearch,options:{
  signal:AbortSignal;load:(at:FolderLocation,signal:AbortSignal)=>Promise<ExplorerListing>;onProgress:(value:SearchProgress)=>void;
  limits?:{folders:number;entries:number;results:number;milliseconds:number};
}) {
  const {signal,load,onProgress}=options;
  const limits=options.limits||{folders:250,entries:50000,results:1000,milliseconds:60000};
  const until=Date.now()+limits.milliseconds,queue=[at],visited=new Set<string>();
  const state:SearchProgress={files:[],folders:0,checked:0,skipped:0,limited:false};
  while(queue.length){
    signal.throwIfAborted();
    if(state.folders>=limits.folders||state.checked>=limits.entries||state.files.length>=limits.results||Date.now()>=until){state.limited=true;break;}
    const folder=queue.shift()!,key=JSON.stringify(folder);if(visited.has(key))continue;visited.add(key);
    const controller=new AbortController(),abort=()=>controller.abort();signal.addEventListener('abort',abort,{once:true});
    const timer=setTimeout(abort,Math.min(12000,Math.max(1,until-Date.now())));
    let listing:ExplorerListing;
    try { listing=await load(folder,controller.signal); }
    catch(error){signal.throwIfAborted();if(!state.folders)throw error;state.skipped++;state.folders++;onProgress({...state,files:[...state.files]});continue;}
    finally {clearTimeout(timer);signal.removeEventListener('abort',abort);}
    signal.throwIfAborted();state.folders++;
    for(const file of listing.files){
      if(state.checked>=limits.entries||state.files.length>=limits.results){state.limited=true;break;}
      state.checked++;
      // Only a single child name from the server can extend this folder.
      if(!file.name||/[\\/]/.test(file.name)||file.name==='.'||file.name==='..')continue;
      const location=childLocation(folder,file.name);
      if(matchesSearch(file,value))state.files.push({...file,location});
      if(value.recursive&&file.isFolder&&queue.length+visited.size<limits.folders)queue.push(location);
      else if(value.recursive&&file.isFolder)state.limited=true;
    }
    onProgress({...state,files:[...state.files]});
    if(!value.recursive)break;
    if(state.checked>=limits.entries||state.files.length>=limits.results){if(queue.length)state.limited=true;break;}
  }
  return state;
}
